import { formatMoney } from "../helpers/money";
const positiveMinutes = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 0 ? Math.round(value) : null;
  }

  const text = String(value ?? "").trim();
  if (/^\d+$/.test(text)) {
    const minutes = Number(text);
    return minutes > 0 ? minutes : null;
  }

  const duration = text.match(/^(\d{1,3}):([0-5]\d)(?::[0-5]\d)?$/);
  if (!duration) return null;
  const minutes = (Number(duration[1]) * 60) + Number(duration[2]);
  return minutes > 0 ? minutes : null;
};

export const minutesToClock = (value) => {
  const minutes = positiveMinutes(value);
  if (!minutes) return "";
  const hours = Math.floor(minutes / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
};

const tierRelation = (staffOrTier) => (
  staffOrTier?.staffservicesprice ||
  staffOrTier?.serviceprice ||
  staffOrTier?.service_price ||
  staffOrTier ||
  {}
);

const tierDuration = (staffOrTier) => {
  const relation = tierRelation(staffOrTier);
  return positiveMinutes(
    relation.duration ??
    relation.duration_minutes ??
    relation.effective_duration ??
    staffOrTier?.effective_duration ??
    staffOrTier?.effective_duration_minutes,
  );
};

export const buildServiceTierSelection = (service = {}, staffOrTier = {}) => {
  const relation = tierRelation(staffOrTier);
  const durationMinutes = tierDuration(staffOrTier) || positiveMinutes(service.duration) || 0;
  const paddingMinutes = positiveMinutes(service.padding_time) || 0;
  const configuredPrices = (Array.isArray(service.serviceprice) ? service.serviceprice : [])
    .filter((row) => Number.isFinite(Number.parseFloat(row?.price)));
  const matchingPrice = configuredPrices.find((row) =>
    staffOrTier?.price_tier_id != null && String(row.price_tier_id) === String(staffOrTier.price_tier_id));
  const lowestPrice = configuredPrices.reduce((lowest, row) =>
    !lowest || Number.parseFloat(row.price) < Number.parseFloat(lowest.price) ? row : lowest, null);
  const parsedPrice = Number.parseFloat(relation.price ?? staffOrTier?.price ?? matchingPrice?.price ?? lowestPrice?.price);

  return {
    price: Number.isFinite(parsedPrice) ? parsedPrice : 0,
    durationMinutes,
    paddingMinutes,
    durationWithPaddingMinutes: durationMinutes + paddingMinutes,
    duration: minutesToClock(durationMinutes),
    padding_time: paddingMinutes ? minutesToClock(paddingMinutes) : "00:00",
    durationWithPadding: minutesToClock(durationMinutes + paddingMinutes),
  };
};

export const buildNoPreferenceTierSelection = (service = {}, staffMembers = []) => {
  const selections = (Array.isArray(staffMembers) ? staffMembers : [])
    .map((staff) => buildServiceTierSelection(service, staff));
  const durations = selections.map((selection) => selection.durationMinutes).filter(Boolean);
  const prices = selections.map((selection) => selection.price).filter(Number.isFinite);
  const fallback = buildServiceTierSelection(service);
  const minimumDurationMinutes = durations.length ? Math.min(...durations) : fallback.durationMinutes;
  const maximumDurationMinutes = durations.length ? Math.max(...durations) : fallback.durationMinutes;
  const price = prices.length ? Math.max(...prices) : fallback.price;

  return {
    ...fallback,
    price,
    durationMinutes: maximumDurationMinutes,
    durationWithPaddingMinutes: maximumDurationMinutes + fallback.paddingMinutes,
    duration: minutesToClock(maximumDurationMinutes),
    durationWithPadding: minutesToClock(maximumDurationMinutes + fallback.paddingMinutes),
    minimumDurationMinutes,
    maximumDurationMinutes,
    durationLabel: minimumDurationMinutes === maximumDurationMinutes
      ? `${maximumDurationMinutes} min`
      : `${minimumDurationMinutes}–${maximumDurationMinutes} min`,
  };
};

export const formatTierPriceAndDuration = (price, duration, maximumDuration, currency) => {
  const amount = Number.parseFloat(price);
  const minimum = positiveMinutes(duration);
  const maximum = positiveMinutes(maximumDuration);
  const durationLabel = minimum && maximum && minimum !== maximum
    ? `${Math.min(minimum, maximum)}–${Math.max(minimum, maximum)} min`
    : `${minimum || maximum || 0} min`;
  return `${formatMoney(Number.isFinite(amount) ? amount : 0, currency)} • ${durationLabel}`;
};
